import { useEffect, useCallback, useMemo, useState } from "react";
import { PanelGroup, Panel, PanelResizeHandle } from "react-resizable-panels";
import { GripHorizontal } from "lucide-react";
import BrandBar from "@/components/layout/BrandBar";
import PreviewArea from "@/components/layout/PreviewArea";
import FolderArea from "@/components/layout/FolderArea";
import WidgetArea from "@/components/layout/WidgetArea";
import HistoryArea from "@/components/layout/HistoryArea";
import StorageBar from "@/components/layout/StorageBar";
import InfoBar from "@/components/layout/InfoBar";
import NoticeDialog from "@/components/shared/NoticeDialog";
import { seedHoverTestData } from "@/lib/db";
import { useAppData, emitDataChanged } from "@/hooks/useAppData";
import DndProvider, { type DragPayload } from "@/components/shared/DndProvider";
import { useKeyboardShortcuts } from "@/hooks/useKeyboardShortcuts";
import { handleCrossAreaDrop } from "@/lib/dndHandlers";
import { getLastPreview } from "@/events/previewBus";
import { clearClipboardIfMatches } from "@/lib/clipboardUtils";
import { GlobalSelectionContext, useGlobalSelectionProvider } from "@/hooks/useGlobalSelection";
import { deleteSelectedItems } from "@/lib/deleteActions";
import DeleteConfirmDialog from "@/components/shared/DeleteConfirmDialog";
import { useIsMobile } from "@/hooks/use-mobile";
import MobileOnly from "@/components/layout/MobileOnly";

import ExportImportButtons from "@/components/layout/ExportImportButtons";
import { useAreaSelection } from "@/hooks/useAreaSelection";
import { WidgetPageStateContext, useWidgetPageStateProvider } from "@/hooks/useWidgetPageState";

const ResizeHandle = () => (
  <PanelResizeHandle className="group relative flex items-center justify-center py-1">
    <div className="h-1 w-16 rounded-full bg-border/40 group-hover:bg-primary/50 group-active:bg-primary/70 transition-all duration-200" />
    <GripHorizontal className="absolute w-4 h-4 text-muted-foreground/30 group-hover:text-primary/60 transition-colors duration-200" />
  </PanelResizeHandle>
);

const Index = () => {
  const globalSelection = useGlobalSelectionProvider();
  const { widgets, widgetPages, history, folderLinks, folders } = useAppData();
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [showNotice, setShowNotice] = useState(false);
  const clearWidgetSelection = useCallback(() => {
    globalSelection.clearArea('widget');
  }, [globalSelection.clearArea]);
  const widgetPageState = useWidgetPageStateProvider(widgetPages, clearWidgetSelection);
  const currentPageWidgets = useMemo(
    () => widgets.filter((widget) => widget.pageId === widgetPageState.currentPageId),
    [widgetPageState.currentPageId, widgets],
  );

  useEffect(() => {
    const lastNoticeDate = localStorage.getItem("lastNoticeDate");
    const today = new Date().toDateString();

    if (lastNoticeDate !== today) {
      setShowNotice(true);
    }

    const params = new URLSearchParams(window.location.search);
    if (!params.has("seedTestData")) return;

    void (async () => {
      await seedHoverTestData();
      emitDataChanged();
    })();
  }, []);

  const handleEscape = useCallback(async () => {
    const lastPreview = getLastPreview();
    globalSelection.clearAll();

    if (lastPreview) {
      await clearClipboardIfMatches(lastPreview.content);
    }
  }, [globalSelection]);

  const handleDelete = useCallback(() => {
    if (globalSelection.totalCount > 0) {
      setShowDeleteDialog(true);
    }
  }, [globalSelection.totalCount]);

  const handleConfirmDelete = useCallback(async () => {
    await deleteSelectedItems(globalSelection.entries);
    globalSelection.clearAll();
    setShowDeleteDialog(false);
  }, [globalSelection]);

  const { handleSelectAll } = useAreaSelection({
    globalSelection,
    widgets: currentPageWidgets,
    history,
    folderLinks
  });

  useKeyboardShortcuts({ onEscape: handleEscape, onDelete: handleDelete, onSelectAll: handleSelectAll });

  const handleDragEnd = useCallback((payload: DragPayload, targetArea: string, targetFolderId?: string) => {
    void handleCrossAreaDrop(payload, targetArea, targetFolderId, widgetPageState.currentPageId);
  }, [widgetPageState.currentPageId]);

  const isMobile = useIsMobile();

  if (isMobile) {
    return <MobileOnly />;
  }

  return (
    <GlobalSelectionContext.Provider value={globalSelection}>
      <WidgetPageStateContext.Provider value={widgetPageState}>
        <DndProvider onDragEnd={handleDragEnd}>
          <div className="h-screen flex flex-col p-3 gap-0">
            <NoticeDialog open={showNotice} onOpenChange={setShowNotice} />
            <div className="shrink-0 mb-3 relative z-[200]">
              <BrandBar onIconClick={() => setShowNotice(true)} />
            </div>

            <PanelGroup direction="vertical" className="flex-1 min-h-0">
              <Panel defaultSize={30} minSize={15}>
                <div className="h-full min-h-0">
                  <PreviewArea />
                </div>
              </Panel>

              <ResizeHandle />

              <Panel defaultSize={70} minSize={30}>
                <div className="h-full min-h-0 overflow-hidden">
                  <div className="grid grid-cols-[minmax(0,240px)_1fr_minmax(0,220px)] gap-3 h-full min-h-0">
                    <FolderArea />
                    <WidgetArea />
                    <HistoryArea />
                  </div>
                </div>
              </Panel>
            </PanelGroup>

            <div className="shrink-0 mt-3 grid grid-cols-[minmax(0,240px)_1fr_minmax(0,220px)] gap-3">
              <div className="col-span-2">
                <StorageBar />
              </div>
              <div className="min-w-0 overflow-hidden h-full">
                <ExportImportButtons />
              </div>
            </div>

            <div className="shrink-0 mt-3">
              <InfoBar />
            </div>
          </div>

          <DeleteConfirmDialog
            open={showDeleteDialog}
            onOpenChange={setShowDeleteDialog}
            onConfirm={handleConfirmDelete}
            count={globalSelection.totalCount}
          />
        </DndProvider>
      </WidgetPageStateContext.Provider>
    </GlobalSelectionContext.Provider>
  );
};

export default Index;
